import { IconOtherClose } from '@doltech/icons/IconOtherClose';
import { MarkedTypeEnum, TestModeEnum } from '@doltech/utils/lib/constants';
import { isNumber } from '@doltech/utils/lib/number';
import { Tooltip } from 'antd';
import cl from 'classnames';
import { isNil } from 'lodash';
import * as React from 'react';
import styled from 'styled-components';

import { colorsV2 } from '../colors-v2';
import { getTooltipTitle } from '../MultipleChoiceQuestionResult/MultipleChoiceQuestionResult';
import { QuestionV2 } from '../Question/QuestionV2';
import { QuestionContainer } from './components';

interface DragOutMobileProps extends React.HTMLAttributes<HTMLSpanElement> {
  number?: number | string;
  answer?: string;
  text?: string;
  onRemove?: () => void;
}

export const DragOutMobileBase = (props: DragOutMobileProps, ref: React.Ref<HTMLSpanElement>) => {
  const { className = 'default', number, answer, text, onRemove, ...rest } = props;
  return (
    <DragOutMobileContainer className={cl(className)} ref={ref} {...rest}>
      {isNumber(number) ? <QuestionV2 no={number} noArrow style={{ marginRight: 8 }} /> : null}
      {answer !== null ? <span className={cl('answer')}>{answer}</span> : null}
      {text ? <span className={cl('text')}>{text}</span> : null}
      {className.includes('filled') ? (
        <Button onClick={onRemove}>
          <IconOtherClose />
        </Button>
      ) : null}
    </DragOutMobileContainer>
  );
};

const Button = styled.button`
  outline: 0;
  border: 0;
  padding: 4px;
  border-radius: 100%;
  background: transparent;
  cursor: pointer;
  color: ${colorsV2.gray100};
  transition: 300ms;

  &:hover {
    color: ${colorsV2.black100};
  }
`;

const DragOutMobileContainer = styled(QuestionContainer)`
  display: flex;

  .text {
    flex: 1;
  }

  .answer.no-margin-right {
    margin-right: 0;
  }

  .delimiter {
    color: ${colorsV2.black100};
    font-weight: 500;
    font-size: 16px;
    margin: 0 8px;
  }

  &.default {
    border: 1px dashed ${colorsV2.gray20};

    .text {
      color: ${colorsV2.gray80};
    }
  }

  &.filled {
    border: 1px solid ${colorsV2.gray20};
  }

  &.no-focus {
    border: 1px solid ${colorsV2.gray20};
  }

  &.in-correct {
    background-color: ${colorsV2.primary10};

    .text {
      flex: initial;
      font-weight: 500;
    }
  }

  &.correct {
    background-color: ${colorsV2.green10};
    .text {
      font-weight: 500;
    }
  }

  &.missed {
    border: 1px dashed ${colorsV2.gray20};
    .text {
      flex: initial;
      font-weight: 500;
    }
  }

  &.empty {
    .answer {
      background: ${colorsV2.gray10};
      border: 1px dashed ${colorsV2.gray60};
    }
  }
`;

export const DragOutMobile = React.memo(React.forwardRef(DragOutMobileBase));

interface DragOutMobileReusltProps extends React.HTMLAttributes<HTMLSpanElement> {
  type: MarkedTypeEnum;
  number: string | number;
  correctAnswer?: string;
  userAnswer?: string;
  correctText?: string;
  userText?: string;
  missedLabel?: string;

  testMode?: TestModeEnum;
}

export const DragOutMobileResult = React.memo((props: DragOutMobileReusltProps) => {
  const { type, number, correctAnswer, userAnswer, correctText, userText, testMode, ...rest } =
    props;

  const tooltipTitle =
    testMode !== TestModeEnum.EXPLANATION ? getTooltipTitle({ markedStatus: type }) : '';
  switch (type) {
    case MarkedTypeEnum.CORRECT:
      return (
        <Tooltip title={tooltipTitle}>
          <DragOutMobile
            number={number}
            answer={correctAnswer}
            text={correctText}
            className="correct"
            {...rest}
          />
        </Tooltip>
      );
    case MarkedTypeEnum.WRONG:
      return (
        <Tooltip title={tooltipTitle}>
          <DragOutMobileContainer className="in-correct" {...rest}>
            <QuestionV2 no={number} noArrow marginRight={8} />
            <span className={cl('answer', { 'no-margin-right': isNil(userText) })}>
              {userAnswer}
            </span>
            <span className="text">{userText}</span>
            <span className="delimiter">/</span>
            <CorrectContainer>
              <span className="answer">{correctAnswer}</span>
              <span className="text">{correctText}</span>
            </CorrectContainer>
          </DragOutMobileContainer>
        </Tooltip>
      );
    case MarkedTypeEnum.MISSING:
      return (
        <Tooltip title={tooltipTitle}>
          <DragOutMobileContainer className="missed" {...rest}>
            <QuestionV2 no={number} noArrow marginRight={8} />
            <CorrectContainer>
              <span className="answer">{correctAnswer}</span>
              <span className="text">{correctText}</span>
            </CorrectContainer>
          </DragOutMobileContainer>
        </Tooltip>
      );

    default:
      throw new Error('type is required');
  }
});

const CorrectContainer = styled(QuestionContainer).attrs({ className: 'correct inline' })`
  &.inline {
    padding: 0;
    border: none;
    margin: 0;

    & > .text {
      font-size: 14px;
      font-weight: 500;
      color: ${colorsV2.black100};
      line-height: 20px;
    }
  }
`;
